import React, { useState, useMemo } from "react";
import {
  ModalBody,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Label,
  Button,
  FormGroup,
  Modal,
  ModalHeader,
  ModalFooter,
  Input,
} from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { H4, P } from "../../../../../AbstractElements";
import Alerts from "../../../../../CommonElements/Alert";

import {
  userRoleService,
  USER_ROLE_QUERY_KEY,
} from "../../../../../Services/userRoleService";
import {
  userService,
  USER_DETAIL_QUERY_KEY,
  PasswordResetPayload,
  ViewuserService,
  USER_VIEW_QUERY_KEY,
} from "../../../../../Services/userService";
import { UserRole } from "../../../../../Types/UserRole/UserRoleModel";
import { Role } from "../../../../../Types/Role/RoleModel";
import {
  ROLE_QUERY_KEY,
  rolesService,
} from "../../../../../Services/rolesService";
import { User } from "../../../../../Types/User/UserModel";
import { ViewUser } from "../../../../../Types/User/ViewUserModel";

// 1. Password Generator Function (Needed for reset)
const generateSecurePassword = (): string => {
  const length = 12;
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  const symbols = "!@#$%^&*()_+=-";
  const all = lower + upper + numbers + symbols;

  let password = "";
  password += lower[Math.floor(Math.random() * lower.length)];
  password += upper[Math.floor(Math.random() * upper.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  return password
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");
};

interface UserManagementModalFormProps {
  toggle?: () => void;
  emp_no: string;
}

// Renamed component for better context
const UserManagementModalForm = ({
  toggle,
  emp_no,
}: UserManagementModalFormProps) => {
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  // State for Password Reset Modal (New)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");

  // --- Data Fetching ---
  const { data: userDetails, isLoading: loadingUserDetails } =
    useQuery<User | null>({
      queryKey: [USER_DETAIL_QUERY_KEY, emp_no],
      queryFn: () => userService.getUserDetailsByEmpNo(emp_no),
      enabled: !!emp_no,
    });

  let user_Id = userDetails?.id || 0;
  const { data: userInfo, isLoading: loadingUserInfo } =
    useQuery<ViewUser | null>({
      queryKey: [USER_VIEW_QUERY_KEY, user_Id],
      queryFn: () => ViewuserService.getUserViewById(user_Id),
      enabled: !!userDetails?.id,
    });
  const isUserLocked = userDetails?.status === "LOCK";
  const isUserBlocked = userDetails?.status === "BLKD";
  const userId = userDetails?.id;
  // --- Mutations ---

  // 1. Unlock Mutation (Existing)
  const unlockMutation = useMutation({
    mutationFn: () => userService.unlockUser(userId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [USER_DETAIL_QUERY_KEY, emp_no],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      toast.success(`Account for ${emp_no} has been unlocked.`);
    },
    onError: (error) => {
      console.error("Failed to unlock user", error);
      toast.error("Failed to unlock user account.");
    },
  });

  const unblockMutation = useMutation({
    mutationFn: () => userService.unblockUser(userId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [USER_DETAIL_QUERY_KEY, emp_no],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      toast.success(`Account for ${emp_no} has been unblocked.`);
    },
    onError: (error) => {
      console.error("Failed to unblock user", error);
      toast.error("Failed to unblock user account.");
    },
  });

  const blockMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason: string }) =>
      userService.blockUser(userId, { reason }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [USER_DETAIL_QUERY_KEY, emp_no],
      });
      queryClient.invalidateQueries({ queryKey: ["users"] });

      toast.success(`Account for ${emp_no} has been blocked.`);
    },

    onError: (error) => {
      console.error("Failed to block user", error);
      toast.error("Failed to block user account.");
    },
  });

  // 2. Password Reset Mutation (New)
  const resetPasswordMutation = useMutation({
    mutationFn: (newPassword: string) => {
      if (!userId) {
        return Promise.reject(new Error("User ID is not available for reset."));
      }
      const payload: PasswordResetPayload = {
        password: newPassword,
        password_confirmation: newPassword,
      };

      // NOTE: Using the 'resetPassword' function added to userService previously
      return userService.resetPassword(userId, payload);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [USER_DETAIL_QUERY_KEY, emp_no],
      });
      // Assumes the API returns the password in data.password
      setGeneratedPassword(data.password);
      setPasswordModalOpen(true);
      toast.success(`Password successfully reset for ${emp_no}.`);
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      let errorMessage = "Failed to reset password.";
      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        errorMessage = responseData.errors[firstErrorKey][0];
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }
      toast.error(errorMessage);
    },
  });

  // 3. Role Assignment Mutations (Existing)
  const { data: userRolesData = [], isLoading: loadingUserRoles } = useQuery({
    queryKey: [USER_ROLE_QUERY_KEY, emp_no],
    queryFn: () => userRoleService.getAllUserRole(emp_no),
    enabled: !!emp_no,
  });
  const { data: allRolesData = [], isLoading: loadingAllRoles } = useQuery({
    queryKey: ROLE_QUERY_KEY,
    queryFn: rolesService.getAllRole,
  });

  const assignedRoles: UserRole[] = Array.isArray(userRolesData)
    ? userRolesData
    : userRolesData.data || [];
  const allRoles: Role[] = Array.isArray(allRolesData)
    ? allRolesData
    : allRolesData.data || [];

  const assignedRoleIds = useMemo(() => {
    return new Set(assignedRoles.map((r) => r.id));
  }, [assignedRoles]);

  const assignMutation = useMutation({
    mutationFn: (ids: number[]) => userRoleService.assignRoles(emp_no, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [USER_ROLE_QUERY_KEY, emp_no],
      });
      setSelectedRoleIds([]);
      toast.success("Roles assigned successfully!");
    },
    onError: (error) => {
      console.error("Failed to assign roles", error);
      toast.error("Failed to assign roles.");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (roleId: number) => userRoleService.removeRole(emp_no, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_VIEW_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [USER_ROLE_QUERY_KEY, emp_no],
      });
      toast.success("Role removed successfully!");
    },
    onError: (error) => {
      console.error("Failed to remove role", error);
      toast.error("Failed to remove role.");
    },
  });

  // --- Handlers ---
  const handleUnlock = async () => {
    if (!userId) {
      toast.error("Cannot unlocked account: User not found.");
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      html: `
           Unlock Account: <strong>${emp_no} - ${userInfo?.emp_name}</strong>`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    });

    if (!result.isConfirmed) return;

    unlockMutation.mutate();
  };

  const handleUnblock = async () => {
    if (!userId) {
      toast.error("Cannot unblocked account: User not found.");
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      html: `
           Unblock Account: <strong>${emp_no} - ${userInfo?.emp_name}</strong>`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    });

    if (!result.isConfirmed) return;

    unblockMutation.mutate();
  };

  const handleBlockUser = async () => {
    if (!userId) {
      toast.error("Cannot block account: User not found.");
      return;
    }

    try {
      // Ask for reason
      const { value: reason } = await Swal.fire({
        text: "Enter reason for blocking this user",
        input: "text",
        inputPlaceholder: "Reason",
        confirmButtonText: "Block User",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        cancelButtonColor: "#dc3545",
      });

      if (!reason) return; // Cancelled or empty

      // Confirm action
      const confirmResult = await Swal.fire({
        icon: "warning",
        html: `Block Account: <strong>${emp_no} - ${userInfo?.emp_name}</strong><br><small>Reason: ${reason}</small>`,
        showCancelButton: true,
        cancelButtonText: "Cancel",
        cancelButtonColor: "#dc3545",
        confirmButtonText: "Block Account",
        confirmButtonColor: "#0d6efd",
      });

      if (!confirmResult.isConfirmed) return;

      // Trigger mutation with variables
      blockMutation.mutate({ userId, reason });
    } catch (err) {
      console.error(err);
      Swal.fire("Oops!", "Something went wrong.", "error");
    }
  };

  // New handler for password reset
  const handlePasswordReset = async () => {
    if (!userId) {
      toast.error("Cannot reset password: User not found.");
      return;
    }

    const newPassword = generateSecurePassword();

    const result = await Swal.fire({
      icon: "warning",
      html: `Confirm action:<br/><br/>
           Generate and reset password for <strong>${emp_no}</strong>
           to a secure temporary password?`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    });

    if (!result.isConfirmed) return;

    resetPasswordMutation.mutate(newPassword);
  };

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  const handleCheckboxChange = (roleId: number) => {
    setSelectedRoleIds((prev) => {
      if (prev.includes(roleId)) return prev.filter((id) => id !== roleId);
      return [...prev, roleId];
    });
  };

  const handleSave = () => {
    if (selectedRoleIds.length > 0) {
      assignMutation.mutate(selectedRoleIds);
    }
  };

  const handleRemove = async (roleId: number) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Remove Role",
      html: "Are you sure you want to remove this role?",
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    });

    if (!result.isConfirmed) return;

    removeMutation.mutate(roleId);
  };

  const closeModalAndFinish = () => {
    setPasswordModalOpen(false);
  };

  // FIX: Using 'username' instead of the unavailable 'employee_details'
  const displayUserName = userDetails?.username || "Loading User...";
  const displayReason = userDetails?.reason || "Loading Reason...";

  const isLoading = loadingUserRoles || loadingAllRoles || loadingUserDetails;

  return (
    <>
      <ModalBody className="p-4">
        <Row className="g-4">
          {/* --- HEADER SECTION (Redesigned) --- */}
          <Col md="12" className="text-center mb-2">
            <h5>User Account: {userInfo?.emp_name || "Loading User..."} </h5>
            <p className="text-muted small mb-0">
              Employee ID:{" "}
              <Badge color="light" className="text-dark border px-2">
                {emp_no}
              </Badge>
            </p>
          </Col>

          {/* --- ACCOUNT ACTIONS CARD (Maintenance Grouping - New Design) --- */}
          <Col md="12">
            <Card className="shadow-sm border-secondary-subtle">
              <CardBody className="p-3">
                <h6 className="mb-3 fw-bold text-uppercase text-secondary small">
                  Account Maintenance
                </h6>
                <Row className="g-2">
                  {/* ACCOUNT STATUS/UNLOCK */}
                  <Col md="6">
                    {loadingUserDetails ? (
                      <div className="text-center py-2 border rounded bg-light">
                        <div
                          className="spinner-border text-secondary spinner-border-sm"
                          role="status"
                        />{" "}
                        <span className="small text-muted">
                          Checking status...
                        </span>
                      </div>
                    ) : isUserBlocked ? (
                      <Button
                        color="danger"
                        className="w-100"
                        onClick={handleUnblock}
                        disabled={unblockMutation.isPending || !userId}
                      >
                        {unblockMutation.isPending ? (
                          <>
                            <i className="fa fa-spinner fa-spin me-1" />{" "}
                            Unblocking...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-unlock me-1" /> Unblock Account
                          </>
                        )}
                      </Button>
                    ) : isUserLocked ? (
                      <Button
                        color="danger"
                        className="w-100"
                        onClick={handleUnlock}
                        disabled={unlockMutation.isPending || !userId}
                      >
                        {unlockMutation.isPending ? (
                          <>
                            <i className="fa fa-spinner fa-spin me-1" />{" "}
                            Unlocking...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-unlock me-1" /> Unlock Account
                          </>
                        )}
                      </Button>
                    ) : (
                      <div>
                        <div className="text-center py-2 border border-success rounded bg-light-success text-success fw-bold small mb-2">
                          <i className="fa fa-check-circle me-1"></i> Account
                          Active
                        </div>

                        <Col md={12}>
                          <Button
                            color="danger"
                            className="w-100"
                            onClick={handleBlockUser}
                            disabled={
                              resetPasswordMutation.isPending ||
                              !userId ||
                              isUserLocked ||
                              isUserBlocked
                            }
                          >
                            {resetPasswordMutation.isPending ? (
                              <>
                                <i className="fa fa-spinner fa-spin me-1" />{" "}
                                Blocking User...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-lock me-1" /> Block User
                              </>
                            )}
                          </Button>
                        </Col>
                      </div>
                    )}
                  </Col>

                  {/* PASSWORD RESET (New) */}
                  <Col md="6">
                    <Button
                      color="warning"
                      className="w-100"
                      onClick={handlePasswordReset}
                      disabled={
                        resetPasswordMutation.isPending ||
                        !userId ||
                        isUserLocked ||
                        isUserBlocked
                      }
                    >
                      {resetPasswordMutation.isPending ? (
                        <>
                          <i className="fa fa-spinner fa-spin me-1" />{" "}
                          Resetting...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-refresh me-1" /> Reset Password
                        </>
                      )}
                    </Button>
                  </Col>
                  {/* Display Lockout Alert if locked */}
                  {isUserLocked && (
                    <Col md="12" className="mt-3">
                      <Alerts
                        color="danger"
                        className="p-2 mb-0 small text-center"
                      >
                        <i className="fa fa-exclamation-triangle me-2"></i>{" "}
                        Account is locked due to login attempts.
                      </Alerts>
                    </Col>
                  )}
                  {isUserBlocked && (
                    <Col md="12" className="mt-3">
                      <Alerts
                        color="danger"
                        className="p-2 mb-0 small text-center"
                      >
                        <i className="fa fa-exclamation-triangle me-2"></i>{" "}
                        {displayReason}. &nbsp;
                        <i className="fa fa-exclamation-triangle me-2"></i>
                      </Alerts>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          </Col>
          {/* --- END ACCOUNT ACTIONS CARD --- */}

          {/* --- ASSIGN NEW ROLES SECTION --- */}
          <Col
            md="12"
            hidden={
              resetPasswordMutation.isPending ||
              !userId ||
              isUserLocked ||
              isUserBlocked
            }
          >
            <h6 className="mb-3 fw-bold text-uppercase text-secondary small">
              Role Maintenance
            </h6>
            <div
              className={`bg-light p-3 rounded border position-relative ${isUserLocked ? "opacity-50" : ""}`}
            >
              <Label className="fw-bold mb-2 small text-uppercase text-secondary">
                Assign New Roles
              </Label>

              <Dropdown
                disaled={isUserBlocked}
                isOpen={dropdownOpen}
                toggle={toggleDropdown}
                direction="down"
              >
                <DropdownToggle
                  caret
                  color="white"
                  className="w-100 text-start d-flex justify-content-between align-items-center border shadow-sm"
                  style={{ backgroundColor: "#fff" }}
                  disabled={isUserLocked || isUserBlocked}
                >
                  {selectedRoleIds.length > 0 ? (
                    <span className="text-primary fw-bold">
                      {selectedRoleIds.length} roles selected
                    </span>
                  ) : (
                    <span className="text-muted">
                      Select roles from list...
                    </span>
                  )}
                </DropdownToggle>

                <DropdownMenu
                  className="w-100 shadow-sm border-0 mt-1"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  {loadingAllRoles && (
                    <DropdownItem disabled>Loading options...</DropdownItem>
                  )}

                  {!loadingAllRoles &&
                    allRoles.map((role) => {
                      const isAlreadyAssigned = assignedRoleIds.has(role.id);
                      return (
                        <div
                          key={role.id}
                          className="dropdown-item py-2 border-bottom"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FormGroup
                            check
                            className="m-0 d-flex align-items-center"
                          >
                            <Input // Using the imported Input now
                              type="checkbox"
                              id={`role-${role.id}`}
                              disabled={
                                isAlreadyAssigned ||
                                isUserLocked ||
                                isUserBlocked
                              }
                              checked={
                                selectedRoleIds.includes(role.id) ||
                                isAlreadyAssigned
                              }
                              onChange={() => handleCheckboxChange(role.id)}
                              style={{ cursor: "pointer" }}
                            />
                            <Label
                              check
                              htmlFor={`role-${role.id}`}
                              className={`ms-2 mb-0 ${
                                isAlreadyAssigned
                                  ? "text-muted small"
                                  : "text-dark fw-medium"
                              }`}
                              style={{ cursor: "pointer", width: "100%" }}
                            >
                              {role.name}
                              {isAlreadyAssigned && (
                                <span className="float-end badge bg-light text-secondary border">
                                  Owned
                                </span>
                              )}
                            </Label>
                          </FormGroup>
                        </div>
                      );
                    })}
                </DropdownMenu>
              </Dropdown>

              {selectedRoleIds.length > 0 && (
                <Button
                  color="primary"
                  className="mt-3 w-100 shadow-sm"
                  onClick={handleSave}
                  disabled={
                    assignMutation.isPending || isUserLocked || isUserBlocked
                  }
                >
                  {assignMutation.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    "Confirm Role Assignment"
                  )}
                </Button>
              )}
            </div>
          </Col>

          {/* --- CURRENT ACCESS HEADER --- */}
          <Col
            md="12"
            className="d-flex align-items-center justify-content-between mb-2"
          >
            <h6 className="mb-0 fw-bold small text-uppercase text-secondary">
              Current Access
            </h6>
            <Badge color="primary" pill>
              {assignedRoles.length}
            </Badge>
          </Col>

          {/* --- CURRENT ACCESS LIST --- */}
          {loadingUserRoles || loadingAllRoles ? (
            <Col md="12" className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </Col>
          ) : assignedRoles.length === 0 ? (
            <Col md="12">
              <div className="text-center py-4 bg-white border border-dashed rounded text-muted">
                <i className="fa fa-folder-open-o fs-3 mb-2 opacity-50"></i>
                <p className="mb-0 small">
                  No roles currently assigned to this user.
                </p>
              </div>
            </Col>
          ) : (
            assignedRoles.map((role: UserRole) => (
              <Col md="12" key={role.id}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    {/* 1. Icon */}
                    <div className="flex-shrink-0 me-3">
                      <div
                        className={`d-flex align-items-center justify-content-center rounded-3 bg-light-${role.color} text-${role.color}`}
                        style={{
                          width: "35px",
                          height: "35px",
                        }}
                      >
                        <i className={`fa ${role.icon} fs-6`}></i>
                      </div>
                    </div>

                    <div className="overflow-hidden">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0 fw-bold text-dark text-truncate small">
                          {role.name}
                        </h6>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 ms-3">
                    <Button
                      color="transparent"
                      className="btn-outline-danger border-0"
                      size="sm"
                      onClick={() =>
                        !removeMutation.isPending && handleRemove(role.id)
                      }
                      disabled={
                        removeMutation.isPending ||
                        !userId ||
                        isUserLocked ||
                        isUserBlocked
                      }
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      title="Remove Role"
                    >
                      {removeMutation.isPending ? (
                        <i className="fa fa-circle-o-notch fa-spin small"></i>
                      ) : (
                        <i className="fa fa-trash-o fs-6"></i>
                      )}
                    </Button>
                  </div>
                </div>
              </Col>
            ))
          )}
        </Row>
      </ModalBody>

      {/* --- PASSWORD DISPLAY MODAL (New) --- */}
      <Modal isOpen={passwordModalOpen} toggle={closeModalAndFinish} centered>
        <ModalHeader toggle={closeModalAndFinish}>
          <i className="fa fa-refresh me-2 text-warning"></i> Password Reset
          Success
        </ModalHeader>
        <ModalBody>
          <P className="text-success fw-bold">
            The password for Employee ID <Badge color="dark">{emp_no}</Badge>{" "}
            has been reset!
          </P>
          <P>
            Please securely provide the following **temporary password** to the
            user. They will be prompted to change it on their first login.
          </P>

          <div className="bg-light p-3 rounded border text-center">
            <Label className="d-block small text-muted text-uppercase mb-1">
              Temporary Password
            </Label>
            <H4
              className="text-primary fw-bold"
              style={{ wordBreak: "break-all" }}
            >
              {generatedPassword}
            </H4>
          </div>

          <P className="mt-3 text-danger small">
            **NOTE:** This password is not stored by the application and will
            not be displayed again.
          </P>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={closeModalAndFinish}>
            I have noted the password
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default UserManagementModalForm;
